import React, { useEffect, useMemo, useState } from 'react';

import cn from 'classnames/bind';
import styles from './styles.styl';
import { Sidebar } from '@consta/uikit/Sidebar';
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { IconClose } from '@consta/uikit/IconClose';
import { Combobox } from '@consta/uikit/Combobox';
import { useShallowEqualSelector } from 'src/customHooks/useShallowEqualSelector';
import { userInitSelector } from 'src/redux/features/userInit/selectors';
import { TextField } from '@consta/uikit/TextField';

const cx = cn.bind(styles);

export interface IAdministrationCurrentFilters {
  fio: string | null;
  role_name: number | null;
}

type SetFilterType = React.Dispatch<
  React.SetStateAction<Partial<IAdministrationCurrentFilters>>
>;

export interface IAdministrationFilterProps {
  isOpen: boolean;
  close: () => void;
  filter: Partial<IAdministrationCurrentFilters>;
  setFilter: SetFilterType;
  clearFilter: () => void;
}

const AdministrationFilter: React.FC<IAdministrationFilterProps> = ({
  isOpen,
  close,
  filter,
  setFilter,
  clearFilter,
}) => {
  const [userInit] = useShallowEqualSelector((state) => [
    userInitSelector(state),
  ]);

  const [localFilter, setLocalFilter] = useState<
    Partial<IAdministrationCurrentFilters>
  >({});

  useEffect(() => {
    isOpen && setLocalFilter(filter);
  }, [filter, isOpen]);

  const closeLocalFilter = () => {
    setLocalFilter({});
    close();
  };

  const filtersAreNotEmpty: boolean = useMemo(
    () =>
      Object.keys(localFilter).some(
        (key: keyof IAdministrationCurrentFilters) => !!localFilter[key],
      ),
    [localFilter],
  );

  const changeFilterValueByKey = (
    key: string,
    value: string | number | boolean | null | string[],
  ) => {
    setLocalFilter((prev: Partial<IAdministrationCurrentFilters>) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyLocalFilter = () => {
    setFilter(localFilter);
    close();
  };

  return (
    <Sidebar
      isOpen={isOpen}
      onClickOutside={close}
      hasOverlay={false}
      className={cx('AdministrationFilter')}
    >
      <Sidebar.Content className={cx('AdministrationFilter__content')}>
        <div className={cx('AdministrationFilter__header')}>
          <Text weight="bold">Фильтры</Text>
          {filtersAreNotEmpty && (
            <Button
              size="xs"
              label="Сбросить все фильтры"
              view="ghost"
              onClick={clearFilter}
            />
          )}
          <IconClose
            size="s"
            className={cx('AdministrationFilter__iconClose')}
            onClick={closeLocalFilter}
          />
        </div>
        {userInit && (
          <>
            <div className={cx('AdministrationFilter__item')}>
              <Text view="secondary">ФИО</Text>
              <TextField
                value={localFilter.fio ?? ''}
                onChange={({ value }) => changeFilterValueByKey('fio', value)}
                size="s"
                placeholder="Введите ФИО для поиска"
              />
            </div>
            <div className={cx('AdministrationFilter__item')}>
              <Text view="secondary">Роль</Text>
              <Combobox
                size="s"
                value={(() => {
                  if (localFilter.role_name) {
                    const item = userInit.roles.find(
                      (item) => item.key === String(localFilter.role_name),
                    );

                    if (!item) return null;
                    return { id: item.key, label: item.name };
                  }

                  return null;
                })()}
                items={userInit.roles.map(({ key, name }) => ({
                  id: key,
                  label: name,
                }))}
                onChange={({ value }) => {
                  changeFilterValueByKey(
                    'role_name',
                    value ? String(value.id) : null,
                  );
                }}
                placeholder="Выберите роль"
              />
            </div>
          </>
        )}
      </Sidebar.Content>
      <Sidebar.Actions className={cx('AdministrationFilter__sidebarActions')}>
        <Button
          size="s"
          label="Отмена"
          view="secondary"
          onClick={closeLocalFilter}
        />
        <Button size="s" label="Применить" onClick={applyLocalFilter} />
      </Sidebar.Actions>
    </Sidebar>
  );
};

export default AdministrationFilter;
